// RailStream API Service
// In production (tower.railstream.net): connects directly to api.railstream.net
// In preview (Emergent): uses backend proxy to avoid CORS issues

// Determine API URL based on environment
const getApiBaseUrl = () => {
  // If REACT_APP_API_URL is set, use it directly (for production)
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // If running on tower.railstream.net, use the production API directly
  if (typeof window !== 'undefined' && window.location.hostname === 'tower.railstream.net') {
    return 'https://api.railstream.net/api';
  }
  
  // For Emergent preview environment, use the backend proxy
  if (process.env.REACT_APP_BACKEND_URL) {
    return `${process.env.REACT_APP_BACKEND_URL}/api`;
  }
  
  // Fallback to relative /api path (for local development with proxy)
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

// Global auth expired flag to prevent redirect storms
let authExpiredFlag = false;
let authExpiredCallbacks = [];

export const onAuthExpired = (callback) => {
  authExpiredCallbacks.push(callback);
  return () => {
    authExpiredCallbacks = authExpiredCallbacks.filter(cb => cb !== callback);
  };
};

export const isAuthExpired = () => authExpiredFlag;

export const resetAuthExpired = () => {
  authExpiredFlag = false;
};

class ApiService {
  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  getToken() {
    return localStorage.getItem('railstream_token');
  }

  setToken(token) {
    localStorage.setItem('railstream_token', token);
  }

  clearToken() {
    localStorage.removeItem('railstream_token');
    localStorage.removeItem('railstream_user');
  }

  getStoredUser() {
    const user = localStorage.getItem('railstream_user');
    return user ? JSON.parse(user) : null;
  }

  setStoredUser(user) {
    localStorage.setItem('railstream_user', JSON.stringify(user));
  }

  // Handle auth expiration - triggers only once
  handleAuthExpired() {
    if (authExpiredFlag) {
      // Already handling auth expiration, don't trigger again
      return;
    }
    
    authExpiredFlag = true;
    this.clearToken();
    
    // Notify all listeners (AuthContext will handle the single redirect)
    authExpiredCallbacks.forEach(cb => {
      try {
        cb();
      } catch (e) {
        console.error('Auth expired callback error:', e);
      }
    });
  }

  async request(endpoint, options = {}) {
    // If auth already expired, don't make more requests
    if (authExpiredFlag) {
      throw new Error('Session expired. Please login again.');
    }

    const token = this.getToken();
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response;
    try {
      response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        headers,
      });
    } catch (networkError) {
      throw new Error('API unreachable. Please check your connection.');
    }

    if (response.status === 401) {
      // Don't use window.location.href - let the router handle it
      this.handleAuthExpired();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || error.message || 'Request failed');
    }

    return response.json();
  }

  // Auth endpoints
  async login(username, password) {
    // Reset auth expired flag on login attempt
    resetAuthExpired();
    
    const url = `${this.baseUrl}/auth/login`;
    const body = JSON.stringify({ username, password });
    
    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: body,
      });
    } catch (networkError) {
      throw new Error('API unreachable. Please check your connection.');
    }

    // Read body once as text
    let responseText;
    try {
      responseText = await response.text();
    } catch (readError) {
      throw new Error('Failed to read server response.');
    }

    // Try to parse as JSON
    let data;
    try {
      data = JSON.parse(responseText);
    } catch (parseError) {
      console.error('Non-JSON response:', responseText.substring(0, 200));
      if (responseText.includes('Too many') || responseText.includes('rate limit')) {
        throw new Error('Too many login attempts. Please wait a minute and try again.');
      }
      throw new Error('Server error. Please try again.');
    }

    // Handle error responses
    if (!response.ok) {
      if (response.status === 401) {
        throw new Error('Invalid username or password');
      }
      if (response.status === 403) {
        throw new Error('Not an admin account');
      }
      throw new Error(data.detail || data.message || 'Login failed');
    }

    // Check if 2FA is required
    if (data.requires_2fa) {
      return { requires_2fa: true, temp_token: data.temp_token };
    }
    
    // Success - store token
    if (data.access_token) {
      this.setToken(data.access_token);
      this.setStoredUser(data.user);
    }
    
    return data;
  }

  async verify2FA(tempToken, code) {
    const url = `${this.baseUrl}/auth/verify-2fa`;
    
    let response;
    try {
      response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ temp_token: tempToken, code }),
      });
    } catch (networkError) {
      throw new Error('API unreachable. Please check your connection.');
    }

    if (response.status === 401) {
      throw new Error('Invalid verification code');
    }

    if (!response.ok) {
      let errorDetail = '2FA verification failed';
      try {
        const errorData = await response.json();
        errorDetail = errorData.detail || errorData.message || errorDetail;
      } catch {
        // Ignore JSON parse errors
      }
      throw new Error(errorDetail);
    }

    const data = await response.json();
    
    if (data.access_token) {
      this.setToken(data.access_token);
      this.setStoredUser(data.user);
    }
    
    return data;
  }

  async getProfile() {
    return this.request('/auth/profile');
  }

  logout() {
    this.clearToken();
    resetAuthExpired();
  }

  // Admin Stats
  async getStats() {
    return this.request('/admin/stats');
  }

  // Analytics
  async getAnalyticsSummary(timeRange) {
    return this.request(`/admin/analytics/summary?range=${timeRange}`);
  }

  async getCamerasAnalytics(timeRange) {
    return this.request(`/admin/analytics/cameras?range=${timeRange}`);
  }

  async getRadioAnalytics(timeRange) {
    return this.request(`/admin/analytics/radio?range=${timeRange}`);
  }

  async getCameraAnalytics(cameraId, timeRange) {
    return this.request(`/admin/analytics/cameras/${cameraId}?range=${timeRange}`);
  }

  // Sessions
  async getActiveSessions() {
    return this.request('/admin/sessions?active_only=true&include_stale=false');
  }

  async getSessionHistory(limit = 100, offset = 0) {
    return this.request(`/admin/sessions?active_only=false&include_stale=true&limit=${limit}&offset=${offset}`);
  }

  async getSessions(options = {}) {
    const { activeOnly = true, includeStale = false, limit = 100, user = null } = options;
    const params = new URLSearchParams();
    params.append('active_only', activeOnly.toString());
    params.append('include_stale', includeStale.toString());
    if (limit) params.append('limit', limit.toString());
    if (user) params.append('user', user);
    return this.request(`/admin/sessions?${params.toString()}`);
  }

  async kickSession(sessionId) {
    return this.request(`/admin/sessions/kick/${sessionId}`, {
      method: 'POST',
    });
  }

  async kickUser(username) {
    return this.request(`/admin/sessions/kick-user/${username}`, {
      method: 'POST',
    });
  }

  // Cameras CRUD
  async getCameras() {
    return this.request('/admin/cameras');
  }

  async getCamera(cameraId) {
    return this.request(`/admin/cameras/${cameraId}`);
  }

  async createCamera(cameraData) {
    return this.request('/admin/cameras', {
      method: 'POST',
      body: JSON.stringify(cameraData),
    });
  }

  async updateCamera(cameraId, cameraData) {
    return this.request(`/admin/cameras/${cameraId}`, {
      method: 'PUT',
      body: JSON.stringify(cameraData),
    });
  }

  async deleteCamera(cameraId) {
    return this.request(`/admin/cameras/${cameraId}`, {
      method: 'DELETE',
    });
  }

  // File Upload - uploads to admin/assets/upload
  async uploadFile(file, assetType = 'thumbnail') {
    if (authExpiredFlag) {
      throw new Error('Session expired. Please login again.');
    }

    const token = this.getToken();
    const formData = new FormData();
    formData.append('file', file);
    formData.append('asset_type', assetType);

    let response;
    try {
      response = await fetch(`${this.baseUrl}/admin/assets/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
    } catch (networkError) {
      throw new Error('API unreachable. Please check your connection.');
    }

    if (response.status === 401) {
      this.handleAuthExpired();
      throw new Error('Session expired. Please login again.');
    }

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Upload failed' }));
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  }

  // Settings
  async getSettings() {
    return this.request('/admin/settings');
  }

  async updateSettings(settings) {
    // Remove MongoDB fields that shouldn't be in PATCH
    const { _id, created_at, updated_at, ...updateData } = settings;
    return this.request('/admin/settings', {
      method: 'PATCH',
      body: JSON.stringify(updateData),
    });
  }

  // User Management (enhanced)
  async getUser(usernameOrEmail) {
    return this.request(`/admin/users/${encodeURIComponent(usernameOrEmail)}`);
  }

  async updateUser(username, data) {
    return this.request(`/admin/users/${username}`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async getUserDevices(username) {
    return this.request(`/admin/users/${username}/devices`);
  }

  // User Notes (aMember integration)
  async getUserNotes(username) {
    return this.request(`/admin/users/${username}/notes`);
  }

  async addUserNote(username, content) {
    return this.request(`/admin/users/${username}/notes`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  }

  // Device Management
  async getAllDevices(limit = 100, offset = 0) {
    return this.request(`/admin/devices?limit=${limit}&offset=${offset}`);
  }

  async removeDevice(deviceId) {
    return this.request(`/admin/devices/${deviceId}`, {
      method: 'DELETE',
    });
  }

  // Audit Log
  async getAuditLog(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.action) searchParams.append('action', params.action);
    if (params.admin_user) searchParams.append('admin_user', params.admin_user);
    if (params.target_type) searchParams.append('target_type', params.target_type);
    return this.request(`/admin/audit-log?${searchParams.toString()}`);
  }

  // Ads Analytics
  async getAdsSummary(range = '7d') {
    return this.request(`/admin/ads/summary?range=${range}`);
  }

  async getAdsEvents(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.event_type) searchParams.append('event_type', params.event_type);
    return this.request(`/admin/ads/events?${searchParams.toString()}`);
  }

  // Security - Login Attempts
  async getLoginAttempts(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.limit) searchParams.append('limit', params.limit.toString());
    if (params.skip) searchParams.append('skip', params.skip.toString());
    if (params.username) searchParams.append('username', params.username);
    if (params.ip) searchParams.append('ip', params.ip);
    if (params.success !== undefined) searchParams.append('success', params.success.toString());
    if (params.range) searchParams.append('range', params.range);
    return this.request(`/admin/login-attempts?${searchParams.toString()}`);
  }

  async unblockLoginAttempts(params = {}) {
    const searchParams = new URLSearchParams();
    if (params.ip) searchParams.append('ip', params.ip);
    if (params.username) searchParams.append('username', params.username);
    return this.request(`/admin/login-attempts/unblock?${searchParams.toString()}`, {
      method: 'DELETE',
    });
  }

  // Sessions Management
  async endStaleSessions(olderThanMinutes = 30, username = null) {
    const body = { older_than_minutes: olderThanMinutes };
    if (username) body.username = username;
    return this.request('/admin/sessions/end-stale', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  // 2FA Setup & Management
  async get2FAStatus() {
    return this.request('/admin/2fa/status');
  }

  async setup2FA() {
    return this.request('/admin/2fa/setup', {
      method: 'POST',
    });
  }

  async verifySetup2FA(code) {
    return this.request('/admin/2fa/verify-setup', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  async disable2FA(code) {
    return this.request('/admin/2fa/disable', {
      method: 'DELETE',
      body: JSON.stringify({ code }),
    });
  }

  async regenerateBackupCodes(code) {
    return this.request('/admin/2fa/backup-codes', {
      method: 'POST',
      body: JSON.stringify({ code }),
    });
  }

  // API Documentation
  async getApiDocs() {
    return this.request('/admin/api-docs');
  }

  async getApiDoc(id) {
    return this.request(`/admin/api-docs/${id}`);
  }

  async createApiDoc(data) {
    return this.request('/admin/api-docs', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateApiDoc(id, data) {
    return this.request(`/admin/api-docs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteApiDoc(id) {
    return this.request(`/admin/api-docs/${id}`, {
      method: 'DELETE',
    });
  }

  // API Keys Management (future backend integration)
  async getApiKeys() {
    return this.request('/admin/api-keys');
  }

  async createApiKey(data) {
    return this.request('/admin/api-keys', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async revokeApiKey(keyId) {
    return this.request(`/admin/api-keys/${keyId}`, {
      method: 'DELETE',
    });
  }

  // Shoutcast Statistics - environment-aware
  async getShoutcastStatistics() {
    // Check if we're on production (tower.railstream.net)
    const isProduction = typeof window !== 'undefined' && 
      window.location.hostname === 'tower.railstream.net';
    
    if (isProduction) {
      // On production, use the production API's shoutcast endpoint
      // This requires the backend team to add /api/shoutcast/statistics
      try {
        const response = await fetch('https://api.railstream.net/api/shoutcast/statistics', {
          headers: {
            'Accept': 'application/json',
          }
        });
        if (response.ok) {
          return response.json();
        }
        // If production endpoint doesn't exist, throw to trigger fallback message
        throw new Error('Shoutcast endpoint not available on production API');
      } catch (err) {
        console.error('Production Shoutcast fetch failed:', err);
        throw new Error('Shoutcast data requires backend endpoint. Please contact the API team to add /api/shoutcast/statistics');
      }
    } else {
      // On preview/development, use our backend proxy
      const response = await fetch(`${this.baseUrl}/shoutcast/statistics`);
      if (!response.ok) {
        throw new Error('Failed to fetch Shoutcast data');
      }
      return response.json();
    }
  }

  // Admin User Management
  async getAdminUsers() {
    return this.request('/admin/admin-users');
  }

  async createAdminUser(data) {
    return this.request('/admin/admin-users', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async updateAdminUser(username, data) {
    return this.request(`/admin/admin-users/${username}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deleteAdminUser(username) {
    return this.request(`/admin/admin-users/${username}`, {
      method: 'DELETE',
    });
  }

  async resetAdminUser2FA(username) {
    return this.request(`/admin/admin-users/${username}/reset-2fa`, {
      method: 'POST',
    });
  }

  // Radio Feeds Management
  async getRadioFeeds() {
    return this.request('/admin/radio-feeds');
  }

  async updateRadioFeed(code, data) {
    console.log('API updateRadioFeed called:', { code, data });
    console.log('API URL:', `${this.baseUrl}/admin/radio-feeds/${code}`);
    const result = await this.request(`/admin/radio-feeds/${code}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    console.log('API updateRadioFeed result:', result);
    return result;
  }

  async createRadioFeed(data) {
    return this.request('/admin/radio-feeds', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  // Studio Integration - Get encoder status from studio.railstream.net
  async getStudioStatus() {
    return this.request('/admin/studio/status');
  }
}

export const api = new ApiService();
export default api;
